/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* =============================================================================
   9AND3 PRODUCT PAGE ENHANCEMENTS
   - Dynamic finish upcharge pricing (+$50 for premium finishes)
   - "Per wheel" price label
   - Free shipping badge + lead time trust bar (injected below Add to Cart)
   ============================================================================= */

(function () {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * Format cents as a money string matching the store format, e.g. "$749.00"
   */
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Calculate the total upcharge (in cents) based on all current line-item
   * property select values. Any option whose text contains "+$50" adds 5000¢.
   */
  function calcUpcharge(form) {
    var total = 0;
    var selects = form.querySelectorAll('select[name^="properties["]');
    selects.forEach(function (sel) {
      var selectedText = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
      var match = selectedText.match(/\+\$(\d+)/);
      if (match) {
        total += parseInt(match[1], 10) * 100;
      }
    });
    return total;
  }

  // ── Price Update ─────────────────────────────────────────────────────────────

  function initFinishPricing() {
    var productForms = document.querySelectorAll('form[is="product-form"]');

    productForms.forEach(function (form) {
      // Find the base price element (the large price shown above the form)
      // The theme renders it inside .price.price--large inside product-meta
      var priceEl = document.querySelector('.price.price--large:not(.price--compare)');
      if (!priceEl) return;

      // Store the original base price in cents as a data attribute so we can
      // always recalculate from the true base even after multiple changes.
      if (!priceEl.dataset.basePriceCents) {
        var rawText = priceEl.textContent.trim().replace(/[^0-9.]/g, '');
        var baseCents = Math.round(parseFloat(rawText) * 100);
        priceEl.dataset.basePriceCents = baseCents;
      }

      function updatePrice() {
        var base = parseInt(priceEl.dataset.basePriceCents, 10);
        var upcharge = calcUpcharge(form);
        var total = base + upcharge;

        // Update the displayed price
        priceEl.textContent = formatMoney(total);

        // Show/hide the upcharge note
        var noteEl = document.getElementById('finish-upcharge-note');
        if (noteEl) {
          if (upcharge > 0) {
            noteEl.textContent = '(Includes +' + formatMoney(upcharge) + ' premium finish upcharge)';
            noteEl.style.display = 'block';
          } else {
            noteEl.style.display = 'none';
          }
        }
      }

      // Listen on any select within this form
      form.addEventListener('change', function (e) {
        if (e.target && e.target.matches('select[name^="properties["]')) {
          updatePrice();
        }
      });

      // Also re-run on variant change (theme emits this event)
      document.addEventListener('variant:changed', function (event) {
        var variant = event.detail.variant;
        if (variant) {
          var newBase = variant.price; // already in cents
          priceEl.dataset.basePriceCents = newBase;
          updatePrice();
        }
      });
    });
  }

  // ── Inject "Per Wheel" Label + Upcharge Note ─────────────────────────────────

  // Detect in-stock products via the Liquid-injected data attribute
  function isInStock() {
    return !!document.querySelector('[data-9and3-in-stock="true"]');
  }

  function injectPerWheelLabel() {
    var priceEl = document.querySelector('.price.price--large:not(.price--compare)');
    if (!priceEl) return;

    // In-stock products are priced per set — skip the "per wheel" label entirely
    if (isInStock()) return;

    // If wrapper already exists and still contains the price element, nothing to do
    if (priceEl.closest('#price-per-wheel-wrapper')) return;

    // Remove any stale wrapper/label left over from a previous injection
    // (can happen if the theme re-renders the price container but not our wrapper)
    var staleWrapper = document.getElementById('price-per-wheel-wrapper');
    if (staleWrapper) staleWrapper.replaceWith(...staleWrapper.childNodes);
    var staleNote = document.getElementById('finish-upcharge-note');
    if (staleNote) staleNote.remove();

    // Wrap the price in a flex container so the label sits inline
    var wrapper = document.createElement('div');
    wrapper.id = 'price-per-wheel-wrapper';
    wrapper.style.cssText = 'display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;';

    priceEl.parentNode.insertBefore(wrapper, priceEl);
    wrapper.appendChild(priceEl);

    // "per wheel" label
    var perWheel = document.createElement('span');
    perWheel.id = 'per-wheel-label';
    perWheel.textContent = 'per wheel';
    perWheel.style.cssText = 'font-size:13px; font-weight:500; letter-spacing:0.08em; text-transform:uppercase; opacity:0.55; white-space:nowrap;';
    wrapper.appendChild(perWheel);

    // Upcharge note (hidden by default, shown when premium finish selected)
    var note = document.createElement('span');
    note.id = 'finish-upcharge-note';
    note.style.cssText = 'display:none; font-size:12px; opacity:0.65; margin-top:2px; width:100%;';
    wrapper.parentNode.insertBefore(note, wrapper.nextSibling);
  }

  // ── Watch for theme re-renders of the price container ────────────────────────
  // The Focal theme replaces the price element on variant change, destroying
  // our injected wrapper. We use a MutationObserver on the price-list container
  // so we can immediately re-inject the label whenever that happens.

  function observePriceContainer() {
    var priceListContainer = document.querySelector('.product-meta__price-list-container');
    if (!priceListContainer) return;

    var observer = new MutationObserver(function () {
      // Re-inject label (function guards against double-injection)
      injectPerWheelLabel();
    });

    observer.observe(priceListContainer, { childList: true, subtree: true });
  }

  // ── Pair form fields side by side ─────────────────────────────────────────────
  // Groups related fields into 2-column rows for a cleaner, more compact layout.
  // Targets: Bolt Pattern + Diameter, Wheel Width + Wheel Offset

  function pairFormFields() {
    if (document.querySelector('.9and3-field-row')) return; // already done

    var form = document.querySelector('.product-form');
    if (!form) return;

    // Find all option selectors (variant dropdowns) and line item property fields
    var allSelectors = Array.from(form.querySelectorAll(
      '.product-form__option-selector, .product-form__line-item-property'
    ));

    // Helper: find a field block by its label text (case-insensitive partial match)
    function findByLabel(text) {
      return allSelectors.find(function(el) {
        var label = el.querySelector(
          '.product-form__option-name, .input__block-label, label'
        );
        return label && label.textContent.toLowerCase().includes(text.toLowerCase());
      });
    }

    // Helper: wrap two adjacent elements in a flex row
    function wrapPair(el1, el2) {
      if (!el1 || !el2) return;
      // Only wrap if they're siblings in the same parent
      if (el1.parentElement !== el2.parentElement) return;
      // Don't double-wrap
      if (el1.closest('.9and3-field-row')) return;

      var row = document.createElement('div');
      row.className = '9and3-field-row';
      row.style.cssText = 'display:grid; grid-template-columns:1fr 1fr; gap:10px;';
      el1.parentElement.insertBefore(row, el1);
      row.appendChild(el1);
      row.appendChild(el2);
    }

    // Pair 1: Bolt Pattern + Diameter
    wrapPair(findByLabel('bolt pattern'), findByLabel('diameter'));

    // Pair 2: Wheel Width + Wheel Offset
    wrapPair(findByLabel('wheel width'), findByLabel('wheel offset'));
  }

  // ── Trust Bar (Shipping + Lead Time) ─────────────────────────────────────────

  // Keep the existing timing cell consistent with the explicit metafield notice.
  // Collection membership still controls the unrelated per-set pricing logic.
  function syncShippingTrustBar() {
    var bar = document.getElementById('9and3-trust-bar');
    var info = document.querySelector('.shopify-section--main-product .product__info') ||
      document.querySelector('.product__info');
    if (!bar || !info) return;
    var cell = bar.querySelector('[data-n93-trust-timing]');
    if (!cell) return;
    var notice = info.querySelector('[data-n93-shipping-status]');
    var inStock = info.getAttribute('data-9and3-in-stock') === 'true';
    var label = notice ? notice.getAttribute('data-n93-trust-label') :
      (inStock ? 'In Stock' : 'Lead Time');
    var detail = notice ? notice.getAttribute('data-n93-trust-detail') :
      (inStock ? 'Ready to ship immediately' : '8 weeks standard \u2022 5 weeks rushed');
    var lines = cell.querySelectorAll('span');
    // Compare first so the observer never creates a mutation loop.
    if (lines[0] && lines[0].textContent !== label) lines[0].textContent = label;
    if (lines[1] && lines[1].textContent !== detail) lines[1].textContent = detail;
  }

  function observeShippingStatus() {
    var root = document.querySelector('.shopify-section--main-product');
    if (!root || root._n93ShippingObserver) return;
    root._n93ShippingObserver = new MutationObserver(function (mutations) {
      var metaChanged = mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some(function (node) {
          return node.nodeType === 1 &&
            (node.matches('product-meta, .n93-shipping-status-region') ||
              !!node.querySelector('.n93-shipping-status-region'));
        });
      });
      if (metaChanged) injectTrustBar();
    });
    root._n93ShippingObserver.observe(root, { childList: true, subtree: true });
  }

  function injectTrustBar() {
    if (document.getElementById('9and3-trust-bar')) {
      syncShippingTrustBar();
      return;
    }

    // Best anchor: the product-form div itself — insert the bar as its first child
    // so it appears right at the top of the form section, below price + Shop Pay,
    // before the variant dropdowns. This avoids any timing issues with async elements.
    var anchor = document.querySelector('.product-form');
    // Fallback: below buy buttons block
    if (!anchor) anchor = document.querySelector('[data-block-type="buy-buttons"]');
    if (!anchor) return;

    var leadTimeHTML = isInStock()
      ? '<span class="trust-bar__label">In Stock</span><span class="trust-bar__sub">Ready to ship immediately</span>'
      : '<span class="trust-bar__label">Lead Time</span><span class="trust-bar__sub">~8 weeks standard &bull; Rush ~5 weeks at checkout</span>';

    // Inline styles used throughout to guarantee layout regardless of CSS caching
    var ITEM_STYLE = 'display:flex;align-items:center;gap:10px;padding:10px 12px;background:#fff;box-sizing:border-box;';
    var LABEL_STYLE = 'font-size:11px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;line-height:1.2;color:#111;display:block;';
    var SUB_STYLE = 'font-size:11px;color:#555;line-height:1.4;display:block;';
    var SVG_STYLE = 'width:22px;height:22px;flex-shrink:0;';
    var DIV_STYLE = 'display:flex;flex-direction:column;gap:2px;';

    function makeItem(svgPath, label, sub, extraStyle, attributes) {
      return '<div' + (attributes || '') + ' style="' + ITEM_STYLE + (extraStyle || '') + '">' +
        '<svg style="' + SVG_STYLE + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' + svgPath + '</svg>' +
        '<div style="' + DIV_STYLE + '">' +
          '<span style="' + LABEL_STYLE + '">' + label + '</span>' +
          '<span style="' + SUB_STYLE + '">' + sub + '</span>' +
        '</div>' +
      '</div>';
    }

    var isInStockProduct = !!document.querySelector('[data-9and3-in-stock="true"]');

    // Build items array: 2 per row, load rating spans full width at bottom
    var row1 = '<div style="display:flex;border-bottom:1px solid #ddd;">' +
      makeItem('<path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect x="9" y="11" width="14" height="10" rx="1"/><circle cx="12" cy="20" r="1"/><circle cx="20" cy="20" r="1"/>', 'Free Shipping', 'To all lower 48 states', 'flex:1;border-right:1px solid #ddd;') +
      makeItem('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>', isInStockProduct ? 'In Stock' : 'Lead Time', isInStockProduct ? 'Ready to ship immediately' : '8 weeks standard &bull; 5 weeks rushed', 'flex:1;', ' data-n93-trust-timing') +
    '</div>';

    var row2Items = makeItem('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>', 'Hub-Centric Fit', 'Cut to your exact vehicle specs', 'flex:1;border-right:1px solid #ddd;');

    var weItemHTML = isInStockProduct ? makeItem('<path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>', 'Load Rating', '2,650 lb/wheel (5 &amp; 6 lug) &bull; 4,000 lb/wheel (8 lug)', 'flex:1;') :
      '<div style="' + ITEM_STYLE + 'flex:1;cursor:pointer;" class="trust-bar__item--clickable" data-weight-estimator-trigger role="button" tabindex="0">' +
        '<svg style="' + SVG_STYLE + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="2"/><rect x="6" y="9" width="12" height="5" rx="1"/><text x="12" y="13" text-anchor="middle" font-size="4" font-weight="700" fill="currentColor" stroke="none" font-family="sans-serif">LBS</text><line x1="3" y1="6" x2="1" y2="4"/><line x1="21" y1="6" x2="23" y2="4"/></svg>' +
        '<div style="' + DIV_STYLE + '"><span style="' + LABEL_STYLE + '">Weight Estimate</span><span style="' + SUB_STYLE + 'text-decoration:underline;text-underline-offset:2px;">Click for estimated wheel weight</span></div>' +
      '</div>';

    var row2 = '<div style="display:flex;border-bottom:1px solid #ddd;">' + row2Items + weItemHTML + '</div>';

    var row3 = '<div style="display:flex;">' +
      makeItem('<path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>', 'Load Rating', '2,650 lb/wheel (5 &amp; 6 lug) &bull; 4,000 lb/wheel (8 lug)', 'flex:1;') +
    '</div>';

    var bar = document.createElement('div');
    bar.id = '9and3-trust-bar';
    bar.style.cssText = 'margin-top:8px;margin-bottom:8px;border:1px solid rgb(223,223,223);border-radius:6px;background:#fff;overflow:hidden;box-sizing:border-box;';
    bar.innerHTML = row1 + row2 + (isInStockProduct ? '' : row3);

    anchor.insertAdjacentElement('beforebegin', bar);
    syncShippingTrustBar();
  }

  // ── Merge Quantity + Add to Cart into one row ──────────────────────────────

  function mergeQtyAndATC() {
    // Already merged?
    if (document.querySelector('.qty-atc-row')) return;

    var qtyBlock = document.querySelector('.product-form__quantity');
    var buyBlock = document.querySelector('.product-form__buy-buttons');
    if (!qtyBlock || !buyBlock) return;

    // Remove the "Quantity:" label — the +/- stepper is self-explanatory
    var qtyLabel = qtyBlock.querySelector('.product-form__quantity-label');
    if (qtyLabel) qtyLabel.style.display = 'none';

    // Create wrapper row
    var row = document.createElement('div');
    row.className = 'qty-atc-row';

    // Insert row where the quantity block currently sits
    qtyBlock.parentNode.insertBefore(row, qtyBlock);

    // Move both blocks into the row
    row.appendChild(qtyBlock);
    row.appendChild(buyBlock);
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    // Only run on product pages — check for the product form which is always present
    if (!document.querySelector('.product-form')) return;

    injectPerWheelLabel();
    observePriceContainer();
    initFinishPricing();
    injectTrustBar();
    observeShippingStatus();
    pairFormFields();
    mergeQtyAndATC();
  }

  function initWhenReady() {
    init();
    // Re-run at intervals to catch late-rendered Shopify section blocks
    setTimeout(init, 200);
    setTimeout(init, 600);
    setTimeout(init, 1500);
  }

  document.addEventListener('shopify:section:load', initWhenReady);

  // Run on window load (after all scripts and sections have rendered)
  if (document.readyState === 'complete') {
    initWhenReady();
  } else {
    window.addEventListener('load', initWhenReady);
    // Also try on DOMContentLoaded as an earlier attempt
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(init, 500);
    });
  }

})();


/* =============================================================================
   9AND3 SLIDESHOW ARROWS
   Injects prev/next arrow buttons into every slide-show element on the page.
   Calls the Focal theme's built-in .previous() / .next() methods.
   ============================================================================= */
(function() {
  var SVG_PREV = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polyline points="15 18 9 12 15 6"></polyline></svg>';
  var SVG_NEXT = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polyline points="9 18 15 12 9 6"></polyline></svg>';

  function addArrows(slideshow) {
    // Only add to slideshows with more than 1 slide
    var items = slideshow.querySelectorAll('slide-show-item');
    if (items.length < 2) return;
    // Don't double-inject
    if (slideshow.querySelector('.sw-arrow')) return;

    var prev = document.createElement('button');
    prev.className = 'sw-arrow sw-arrow--prev';
    prev.setAttribute('aria-label', 'Previous slide');
    prev.innerHTML = SVG_PREV;
    prev.addEventListener('click', function(e) {
      e.stopPropagation();
      slideshow.previous();
    });

    var next = document.createElement('button');
    next.className = 'sw-arrow sw-arrow--next';
    next.setAttribute('aria-label', 'Next slide');
    next.innerHTML = SVG_NEXT;
    next.addEventListener('click', function(e) {
      e.stopPropagation();
      slideshow.next();
    });

    // Insert into the slide-show element (which is position:relative in Focal)
    slideshow.appendChild(prev);
    slideshow.appendChild(next);
  }

  function init() {
    document.querySelectorAll('slide-show').forEach(addArrows);
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(init, 300);
  } else {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(init, 300); });
  }
  // Catch any late-rendered sections
  window.addEventListener('load', function() { setTimeout(init, 500); });
})();

/* =============================================================================
   9AND3 RING STYLE GUIDE MODAL
   Injects a "?" button next to the Ring Style label.
   Opens a comparison modal showing Standard vs Slotted ring styles.
   ============================================================================= */

(function () {
  'use strict';

  function initRingStyleGuide() {
    var modal = document.getElementById('ring-style-modal');
    if (!modal) return;

    // ── Inject trigger button next to Ring Style label ──────────────────────
    function injectTrigger() {
      if (document.getElementById('rsm-trigger-btn')) return;

      // Find the Ring Style label — look for option-name span containing "Ring Style"
      var allOptionNames = document.querySelectorAll('.product-form__option-name');
      var ringStyleLabel = null;
      allOptionNames.forEach(function (el) {
        if (el.textContent.trim().toLowerCase().includes('ring style')) {
          ringStyleLabel = el;
        }
      });

      if (!ringStyleLabel) return;

      var btn = document.createElement('button');
      btn.id = 'rsm-trigger-btn';
      btn.className = 'rsm-trigger';
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Ring style guide — see the difference');
      btn.textContent = 'What\'s this?';

      // Make the label a flex container so button sits inline
      ringStyleLabel.style.display = 'inline-flex';
      ringStyleLabel.style.alignItems = 'center';
      ringStyleLabel.appendChild(btn);

      btn.addEventListener('click', openModal);
    }

    // ── Modal open/close ────────────────────────────────────────────────────
    function openModal() {
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      modal.querySelector('.rsm-close').focus();
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }

    // Close button
    var closeBtn = modal.querySelector('.rsm-close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    // Click outside modal box
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    // ── Re-inject trigger after variant changes ───────────────────────────
    // The theme re-renders option selectors on variant change, destroying
    // our injected button. Watch the product form for DOM mutations and
    // re-inject whenever the button disappears.
    var productForm = document.querySelector('.product-form');
    if (productForm) {
      var rsmObserver = new MutationObserver(function () {
        if (!document.getElementById('rsm-trigger-btn')) {
          injectTrigger();
        }
      });
      rsmObserver.observe(productForm, { childList: true, subtree: true });
    }

    // Belt-and-suspenders: also re-inject on variant:changed event
    document.addEventListener('variant:changed', function () {
      setTimeout(function () {
        if (!document.getElementById('rsm-trigger-btn')) {
          injectTrigger();
        }
      }, 100);
    });

    // ── Tabs ────────────────────────────────────────────────────────────────
    var tabs = modal.querySelectorAll('.rsm-tab');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.dataset.tab;

        // Update tab active states
        tabs.forEach(function (t) { t.classList.remove('rsm-tab--active'); });
        tab.classList.add('rsm-tab--active');

        // Update panel active states
        modal.querySelectorAll('.rsm-panel').forEach(function (panel) {
          panel.classList.remove('rsm-panel--active');
        });
        var targetPanel = document.getElementById('rsm-panel-' + target);
        if (targetPanel) targetPanel.classList.add('rsm-panel--active');
      });
    });

    // ── Init ────────────────────────────────────────────────────────────────
    injectTrigger();
    // Re-run after delays to catch late-rendered Shopify/Globo blocks
    setTimeout(injectTrigger, 500);
    setTimeout(injectTrigger, 1500);
  }

  if (document.readyState === 'complete') {
    initRingStyleGuide();
  } else {
    window.addEventListener('load', initRingStyleGuide);
  }

})();
